<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h2 class="text-3xl font-bold text-gray-900">Plans</h2>
      <button @click="showCreateForm = true" class="btn btn-primary">
        Create Plan
      </button>
    </div>

    <div v-if="loading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      <p class="mt-2 text-gray-600">Loading plans...</p>
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Error</h3>
          <div class="mt-2 text-sm text-red-700">{{ error }}</div>
        </div>
      </div>
    </div>

    <div v-else class="card">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Minute Limit</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day Limit</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month Limit</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="plan in plans" :key="plan.name" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ plan.name }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ plan.minuteLimit.toLocaleString() }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ plan.dayLimit.toLocaleString() }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ plan.monthLimit.toLocaleString() }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                <button @click="editPlan(plan)" class="btn btn-primary btn-sm">
                  Edit
                </button>
                <button @click="deletePlan(plan.name)" class="btn btn-danger btn-sm">
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="plans.length === 0" class="text-center py-12">
          <p class="text-gray-500">No plans found. Create your first plan to get started.</p>
        </div>
      </div>
    </div>
    
    <!-- Create/Edit Modal -->
    <div v-if="showCreateForm || editingPlan" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="closeModal">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
        <h3 class="text-lg font-bold text-gray-900 mb-4">{{ editingPlan ? 'Edit Plan' : 'Create Plan' }}</h3>

        <form @submit.prevent="submitForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
            <input
              v-model="formData.name"
              type="text"
              required
              :disabled="!!editingPlan"
              placeholder="Enter plan name"
              class="form-input"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Minute Limit</label>
            <input
              v-model.number="formData.minuteLimit"
              type="number"
              required
              min="1"
              placeholder="Requests per minute"
              class="form-input"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Day Limit</label>
            <input
              v-model.number="formData.dayLimit"
              type="number"
              required
              min="1"
              placeholder="Requests per day"
              class="form-input"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Month Limit</label>
            <input
              v-model.number="formData.monthLimit"
              type="number"
              required
              min="1"
              placeholder="Requests per month"
              class="form-input"
            />
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button type="button" @click="closeModal" class="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" :disabled="submitting">
              {{ submitting ? 'Saving...' : (editingPlan ? 'Update' : 'Create') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { adminApi } from '@/services/api'
import type { Plan, CreateOrUpdatePlan } from '@/types'

const plans = ref<Plan[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const showCreateForm = ref(false)
const editingPlan = ref<Plan | null>(null)
const submitting = ref(false)

const formData = ref({
  name: '',
  minuteLimit: 10,
  dayLimit: 1000,
  monthLimit: 30000
})

const loadPlans = async () => {
  loading.value = true
  error.value = null
  
  try {
    plans.value = await adminApi.getPlans()
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || 'Failed to load plans'
  } finally {
    loading.value = false
  }
}

const editPlan = (plan: Plan) => {
  editingPlan.value = plan
  formData.value = {
    name: plan.name,
    minuteLimit: plan.minuteLimit,
    dayLimit: plan.dayLimit,
    monthLimit: plan.monthLimit
  }
}

const closeModal = () => {
  showCreateForm.value = false
  editingPlan.value = null
  formData.value = {
    name: '',
    minuteLimit: 10,
    dayLimit: 1000,
    monthLimit: 30000
  }
}

const submitForm = async () => {
  submitting.value = true
  
  try {
    const planData: CreateOrUpdatePlan = {
      name: formData.value.name,
      minuteLimit: formData.value.minuteLimit,
      dayLimit: formData.value.dayLimit,
      monthLimit: formData.value.monthLimit
    }
    
    if (editingPlan.value) {
      // Update existing plan
      await adminApi.updatePlan(editingPlan.value.name, planData)
    } else {
      // Create new plan
      await adminApi.createPlan(planData)
    }
    
    closeModal()
    await loadPlans()
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || 'Failed to save plan'
  } finally {
    submitting.value = false
  }
}

const deletePlan = async (name: string) => {
  if (!confirm(`Are you sure you want to delete plan "${name}"?`)) {
    return
  }
  
  try {
    await adminApi.deletePlan(name)
    await loadPlans()
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || 'Failed to delete plan'
  }
}

onMounted(() => {
  loadPlans()
})
</script>


